import { List, Map, Set } from 'immutable';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { ActionTypes, BROWSE_BASE_PATH, DiscoveryKeys } from './constants';
import * as D from './deps';
import BrowseSection from './models/BrowseSection';
import {
  parseAppendedSectionData,
  parseDeepLevelData,
  parseTopLevelData,
} from './models/dataTransforms';

export const INITIAL_STATE = Map({
  [DiscoveryKeys.BROWSE]: Map({
    [DiscoveryKeys.IS_FETCHING]: false,
    [DiscoveryKeys.FETCHING_OFFSETS]: Set(),
    [DiscoveryKeys.TOP_LEVEL_SECTIONS]: List(),
    [DiscoveryKeys.DEEP_LEVEL_SECTIONS]: List(),
    [DiscoveryKeys.IS_INVALID_URL]: false,
    [DiscoveryKeys.WEB_URL]: undefined,
  }),
});

export default D.createReducer(INITIAL_STATE, {
  [D.Constants.Api.ApiActions.PLS_GET_DIRECTORY_ATTEMPT]: setFetchingBrowseKey,
  [D.Constants.Api.ApiActions.PLS_GET_DIRECTORY_SUCCESS]: getDirectorySuccessFromAction,
  [D.Constants.App.AppActions.LEAVE_404]: getBrowseAfter404,
  [ActionTypes.INGEST_BROWSE_DIRECTORY_DATA]: getDirectorySuccessIngestion,
  [D.Constants.Api.ApiActions.CREATE_STATION_SUCCESS]: clearAllBrowseData,
  [D.Constants.Api.ApiActions.REMOVE_STATION_SUCCESS]: clearAllBrowseData,
  [D.GraphQL.Constants.OperationNameActions.GRAPHQL_REMOVE_FROM_LIBRARY_SUCCESS]:
    clearAllBrowseData,
});

function setFetchingBrowseKey(state, action) {
  const fetchingOffset = action.request.content.offset;
  const fetchingOffsets = state
    .getIn([DiscoveryKeys.BROWSE, DiscoveryKeys.FETCHING_OFFSETS])
    .add(fetchingOffset);
  return state
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_FETCHING], true)
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.FETCHING_OFFSETS], fetchingOffsets);
}

function createListOfSections(sections) {
  return List(sections.map(props => new BrowseSection(props)));
}

function handlePaginationResponse(responseData, deepLevelSections, id, path, offset) {
  // Find deep level section that we're looking at based on the path we are on
  const deepLevelSectionToEdit = deepLevelSections.find(section => {
    return section.webUrl === path;
  });

  // Find specific subsection within deep level section that we are on
  // This will always be the _last_ subsection but safer to find by id of subsection
  let sectionToAppend = deepLevelSectionToEdit.subsections.find(subsection => {
    // find specific subsection within deeplevelsection
    return subsection.id === id;
  });

  // If we couldn't find the right deepLevelSection, that means we're handling a single paginated directory
  if (!sectionToAppend) {
    sectionToAppend = deepLevelSectionToEdit;
  }

  const currentPageBeingAdded = D.Utils.RandomAccess.getCurrentPage(
    offset,
    sectionToAppend.totalPages,
    sectionToAppend.currentCount,
  );

  const listOfItems = parseAppendedSectionData(
    responseData,
    sectionToAppend,
    offset,
    currentPageBeingAdded,
  );
  // Update specific subsection with new list of items coming back from pagination
  const updatedSubsection = sectionToAppend.withMutations(subsection => {
    subsection.set('items', listOfItems);
  });

  // find index of specific subsection within deeplevelsection
  const indexToReplaceInDeepLevelSubsections = deepLevelSectionToEdit.subsections.findIndex(
    subsection => {
      return subsection.id === id;
    },
  );

  // Update the list of subsections that the deep level section has
  const updatedDeepLevelSection = deepLevelSectionToEdit.withMutations(subsection => {
    const deepLevelSectionToEditSubsections = deepLevelSectionToEdit.subsections;
    deepLevelSectionToEditSubsections.slice(
      indexToReplaceInDeepLevelSubsections,
      1,
      updatedSubsection,
    );
    subsection.set('subsections', deepLevelSectionToEditSubsections);
  });

  const indexToReplaceInDeepLevel = deepLevelSections.findIndex(sectionToReplace => {
    return sectionToReplace.webUrl === path;
  });

  // Update original list of deep level sections with new deep level section
  deepLevelSections.slice(indexToReplaceInDeepLevel, 1, updatedDeepLevelSection);
  return deepLevelSections;
}

function getBrowseAfter404(state, action) {
  return state.setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_INVALID_URL], false);
}

function getDirectorySuccessFromAction(state, action) {
  if (action.response === null || action.response.errorMessage) {
    return state.setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_INVALID_URL], true);
  }
  const path = get(action, 'response.webUrl');
  const requestId = get(action, 'params.content.directoryId');
  const offset = get(action, 'params.content.offset');
  return onGetDirectorySuccess(state, action.response, path, requestId, offset);
}

export function getDirectorySuccessIngestion(state, { payload }) {
  const data = payload?.[0] ?? {};
  const path = data.webUrl;
  if (isEmpty(path)) {
    return state;
  }
  const requestId = undefined;
  return onGetDirectorySuccess(state, data, path, requestId, 0);
}

/**
 *
 * @param {object} state - redux state
 * @param {object} response - API response
 * @param {string} path - browse URI path for this data
 * @param {string?}requestId - (optional) pandoraId for this data. Used when requesting data not specific to a Browse path, like a Podcast BSP
 * @param {number} offset - (optional) data returned might be paginated off this value
 * @returns {object} state
 */
function onGetDirectorySuccess(state, response, path, requestId, offset) {
  if (response === null || response.errorMessage) {
    return state.setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_INVALID_URL], true);
  }

  if (path === BROWSE_BASE_PATH) {
    // top level data
    return state
      .setIn(
        [DiscoveryKeys.BROWSE, DiscoveryKeys.TOP_LEVEL_SECTIONS],
        createListOfSections(parseTopLevelData(response)),
      )
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_FETCHING], false)
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_INVALID_URL], false)
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.WEB_URL], path);
  }

  let deepLevelSections = state.getIn(
    [DiscoveryKeys.BROWSE, DiscoveryKeys.DEEP_LEVEL_SECTIONS],
    List(),
  );

  // Checking for this condition means we called getDirectory with an offset, which only returns
  // the requested items for ONE subsection
  if (get(response, 'sections[0].subsections[0].totalItems', null) && offset) {
    const fetchingOffsets = state
      .getIn([DiscoveryKeys.BROWSE, DiscoveryKeys.FETCHING_OFFSETS])
      .remove(offset);
    deepLevelSections = handlePaginationResponse(
      response,
      deepLevelSections,
      response.sections[0].id,
      path,
      offset,
    );
    return state
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.DEEP_LEVEL_SECTIONS], deepLevelSections)
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_FETCHING], false)
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.FETCHING_OFFSETS], fetchingOffsets)
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_INVALID_URL], false)
      .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.WEB_URL], path);
  }
  const BrowseSectionRecord = new BrowseSection({
    ...parseDeepLevelData(response),
    requestId,
  });
  deepLevelSections = deepLevelSections.push(BrowseSectionRecord);
  return state
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.DEEP_LEVEL_SECTIONS], deepLevelSections)
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_FETCHING], false)
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.IS_INVALID_URL], false)
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.WEB_URL], path);
}

function clearAllBrowseData(state) {
  /*
  Removes all browse data so components are forced to reload.
  */
  return state
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.TOP_LEVEL_SECTIONS], List())
    .setIn([DiscoveryKeys.BROWSE, DiscoveryKeys.DEEP_LEVEL_SECTIONS], List());
}
